import { Controller } from "stimulus";
import { useDebounce } from "stimulus-use";
import { formatPrice } from "../helpers";
import { useClickOutside } from "stimulus-use";
import { enableBodyScroll, disableBodyScroll } from "body-scroll-lock-upgrade";

export default class extends Controller {
  static targets = [
    "headerSearch",
    "collectionProducts",
    "quickProducts",
    "collections",
    "suggestions",
    "input",
    "panel",
    "query",
    "products",
    "articles",
    "articlesNext",
    "articlesPrevious",
    "productsNext",
    "productsPrevious",
  ];
  static debounces = ["handleSearch"];

  initialize() {
    if (window.location.pathname === "/search") {
      const articlesSection = document.querySelector("#search-articles");
      const options = {
        root: null,
        rootMargin: "20%",
        threshold: 0.5,
      };

      const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            try {
              this.fetchArticles();
            } catch (err) {
              console.log("There was an error fetching articles...", err);
            } finally {
              observer.unobserve(entry.target);
            }
          }
        });
      }, options);

      if (articlesSection) {
        observer.observe(articlesSection);
      }
    }
  }

  connect() {
    useDebounce(this, { wait: 400 });

    if (this.hasHeaderSearchTarget) {
      useClickOutside(this, { element: this.headerSearchTarget });
    }
  }

  toggleSearch(e) {
    e.preventDefault();
    if (this.hasHeaderSearchTarget) {
      this.headerSearchTarget.classList.toggle("header__search--inactive");
      this.inputTarget.value = "";
    }

    const header = document.querySelector(".header");

    if (
      this.panelTarget.classList.contains("header__search-results--visible")
    ) {
      this.clearSearch();
      this.panelTarget.classList.remove("header__search-results--visible");
      enableBodyScroll(this.panelTarget);
      header.style.position = "sticky";
    } else {
      this.inputTarget.focus();

      if (window.innerWidth < 1100) {
        header.style.position = "fixed";
        disableBodyScroll(this.panelTarget);
      }
    }
  }

  triggerSearch(e) {
    e.preventDefault();
  }

  clearSearch() {
    this.inputTarget.value = "";
  }

  async handleSearch(e) {
    const query = e.target.value;

    if (query?.length >= 1) {
      try {
        const res = await fetch(
          `/search/suggest.json?q=${query}&resources[type]=product,collection&resources[options][fields]=body,title,product_type,variants.title,vendor,tag`
        );

        if (res.ok) {
          const data = await res.json();
          const { products, collections } = data.resources.results;

          if (products?.length > 5 && this.hasQuickProductsTarget) {
            this.quickProductsTarget.innerHTML += `
              <div class="tw-flex tw-items-center tw-justify-between">
                <span class="tw-text-xs">TOP RESULTS FOR &apos;${query}&apos;</span>
                <button type="submit" class="header__search-extra tw-my-3">See All Results</button>
              </div>
            `;
          }

          this.renderQuickResults({
            products: products?.slice(0, 5),
            collections,
          });
        } else {
          console.error("there was an error fetching results...", res);
        }
      } catch (error) {
        console.error("there was an error fetching results...", error);
      }
    } else if (query.length === 0) return this.clearSearch();
  }

  clearSearch(e) {
    e?.preventDefault();
    this.inputTarget.value = "";
    document.body.style.overflow = null;
    setTimeout(() => {
      this.collectionsTarget.innerHTML = "";
      this.collectionProductsTarget.style.display = "grid";
      this.quickProductsTarget.style.display = "none";
      this.quickProductsTarget.innerHTML = ``;
    }, 300);
  }

  renderQuickResults({ products, collections }) {
    if (this.hasQuickProductsTarget) {
      this.collectionProductsTarget.style.display = "none";
      this.quickProductsTarget.style.display = "grid";
      this.quickProductsTarget.innerHTML = `
          ${products?.length > 0
          ? products
            .map((product) => {
              let price = product.price;

              if (price === "1.00") {
                // This code is to ignore products classed as samples
                if (product.variants.length > 0) {
                  const _price = product.variants.find(
                    (v) => parseInt(v.price) > 1
                  );

                  if (_price) {
                    price = _price.price;
                  }
                } else {
                  price = product.price_max;
                }
              }

              const image = product.featured_image.url;
              const image_url = image?.split(".png")[0];

              return `
                <article class="header__search-result" data-action="click->navigation#handleClick">
                  <img loading="lazy" height="200" width="200" src="${image_url}_200x200.png" alt="${product.title
                }" />
                  <div class="header__search-result-content">
                    <p class="result__title">${product.title}</p>
                    <p class="result__price">
                      ${formatPrice(
                  price,
                  false,
                  typeof trade_discount !== undefined,
                  false
                )}
                    </p>
                  </div>
                </article>
                `;
            })
            .join("")
          : "No products found..."
        }
      `;

      this.panelTarget.classList.add("header__search-results--visible");
    }

    if (products?.length <= 0) {
      this.quickProductsTarget.innerHTML = `
      <div class="results__products">
        <strong>Products</strong>
        <p style="white-space: nowrap">No products found...</p>
      </div>
      `;
    }

    if (this.hasCollectionsTarget && collections?.length > 0) {
      this.collectionsTarget.innerHTML = `<strong>Collections</strong>`;
      this.collectionsTarget.innerHTML += `
        <div class="results__collections">
          ${collections
          .map((collection) =>
            collection.handle !== "sample-sale"
              ? `
            <a href="${collection.url}">${collection.title}</a>
          `
              : ""
          )
          .join("")}
        </div>
        `;
    }
  }

  openPanel(e) {
    return this.panelTarget.classList.add("header__search-results--visible");
  }

  closePanel() {
    if (this.panelTarget.classList.contains("header__search-results--visible"))
      return this.panelTarget.classList.remove(
        "header__search-results--visible"
      );
  }

  async fetchArticles() {
    const params = new URLSearchParams(window.location.search);
    params.set("q", this.queryTarget.value);
    params.set("view", "articles");
    params.set("type", "article");
    params.set("section_id", "search-articles");
    if (params.get("articles_page")) {
      params.set("page", params.get("articles_page"));
    } else {
      params.set("page", 1);
    }
    const url = `/search?${params.toString()}`;

    await fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;

        document.querySelector("#article-listing").innerHTML = html.innerHTML;
      });
  }

  // Pagination
  async handlePagination(e) {
    const { resource = "products" } = e.params;
    let target = this.productsTarget;
    let nextTarget = this.productsNextTarget;
    let prevTarget = this.productsPreviousTarget;

    if (resource === "articles") {
      target = this.articlesTarget;
      nextTarget = this.articlesNextTarget;
      prevTarget = this.articlesPreviousTarget;
    }

    e.preventDefault();
    const page = new URLSearchParams(e.target.href.split("?")[1]).get("page");

    await fetch(e.target.href)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;

        if (e.target.dataset.direction === "next") {
          target.innerHTML += html.querySelector(
            `.search-${resource}__wrap`
          ).innerHTML;

          const nextPage = html.querySelector(
            `[data-search-target='${resource}Next']`
          );

          if (!nextPage.classList.contains("hidden")) {
            nextTarget.href = nextPage.href;
          } else {
            nextTarget.style.display = "none";
          }
        } else {
          target.innerHTML =
            html.querySelector(`.search-${resource}__wrap`).innerHTML +
            target.innerHTML;

          const prevPage = html.querySelector(
            `[data-search-target='${resource}Previous']`
          );

          if (!prevPage.classList.contains("hidden") && prevPage) {
            prevTarget.href = prevPage.href;
          } else {
            prevTarget.style.display = "none";
          }
        }

        const params = new URLSearchParams(window.location.search);
        if (resource === "articles") {
          params.set(`articles_page`, page);
        } else {
          params.set(`page`, page);
        }

        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}?${params.toString()}`
        );
      })
      .catch((error) => {
        console.log(error);
      });
  }
}
